import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axios/axiosInstance";
import { sanitizePreferredRoleForPayload } from "../utils/profileFieldNormalization";

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const fluencyLevels = ["Basic", "Intermediate", "Business fluent", "Bilingual/Native"];

const normalizeSoftSkill = (skill) => ({
  id: skill.id ?? skill.softSkillId ?? skill.soft_skill_id ?? null,
  softSkill: skill.softSkill ?? skill.name ?? skill.soft_skill ?? "",
  description: skill.description ?? "",
  evidence: skill.evidence ?? "",
});

const getSoftSkillKey = (skill) => {
  if (skill?.id != null) return `id:${skill.id}`;
  if (skill?.softSkillId != null) return `id:${skill.softSkillId}`;
  const name = (skill?.softSkill ?? skill?.name ?? "").trim().toLowerCase();
  return name ? `name:${name}` : null;
};

const isSoftSkillSelected = (selectedList, option) => {
  const optionKey =
    option.id != null
      ? `id:${option.id}`
      : `name:${(option.name || "").trim().toLowerCase()}`;
  return selectedList.some((item) => {
    const itemKey = getSoftSkillKey(item);
    if (itemKey && itemKey === optionKey) return true;
    if (option.id != null && item.id != null && Number(item.id) === Number(option.id)) {
      return true;
    }
    const optionName = (option.name || "").trim().toLowerCase();
    const itemName = (item.softSkill ?? item.name ?? "").trim().toLowerCase();
    return optionName && itemName && optionName === itemName;
  });
};

const languageOptions = [
  "English", "French", "German", "Italian", "Spanish", "Dutch",
  "Portuguese", "Polish", "Czech", "Danish", "Swedish", "Finnish",
  "Norwegian", "Icelandic", "Romanian", "Hungarian", "Greek",
  "Slovak", "Slovenian", "Croatian", "Bulgarian", "Estonian",
  "Latvian", "Lithuanian",
];

// ── Skills dialog (plain HTML) ─────────────────────────────────────────────────
function SkillsDialog({ open, onClose, skillsMap, currentSkills, onSave }) {
  const [selected, setSelected] = useState(currentSkills || []);
  useEffect(() => { if (open) setSelected(currentSkills || []); }, [open, currentSkills]);

  const allSkills = Object.keys(skillsMap || {});

  const toggle = (name) => {
    setSelected((prev) => {
      const exists = prev.find((s) => Object.keys(s)[0] === name);
      return exists
        ? prev.filter((s) => Object.keys(s)[0] !== name)
        : [...prev, { [name]: { level: "Intermediate", subSkills: [] } }];
    });
  };

  if (!open) return null;
  return (
    <div className="cpe-modal-overlay" onClick={onClose}>
      <div className="cpe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="cpe-modal-title">Select Technical Skills</h3>
        {allSkills.length === 0 ? (
          <p className="cpe-helper">No skills available for this role.</p>
        ) : (
          <div className="cpe-checkbox-group" style={{ maxHeight: "300px", overflowY: "auto" }}>
            {allSkills.map((name) => (
              <label key={name} className="cpe-checkbox-item">
                <input
                  type="checkbox"
                  checked={selected.some((s) => Object.keys(s)[0] === name)}
                  onChange={() => toggle(name)}
                />
                {name}
              </label>
            ))}
          </div>
        )}
        <div className="cpe-modal-actions">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={() => { onSave(selected); onClose(); }}>
            Save Skills
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

/**
 * ChooseRolesSection
 *
 * All PreferredRoles fields — plain HTML/CSS.
 *   ✅ Visible to user:   Preferred Role cards (Function + Role), Technical Skills
 *   ⭐ Admin-only hidden: Soft Skills (with evidence), Languages (with fluency)
 */
const ChooseRolesSection = ({ profileData, onChange, validationKey = 0 }) => {
  const src = profileData?.preferredRoles || {};

  const [roles, setRoles] = useState([{ id: 1, function: "", role: "", skills: [] }]);
  const [softSkills, setSoftSkills] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  const [localFunctions, setLocalFunctions] = useState([]);
  const [roleOptions, setRoleOptions] = useState({});
  const [loadingRoles, setLoadingRoles] = useState({});
  const [softSkillList, setSoftSkillList] = useState([]);
  const [loadingSoftSkills, setLoadingSoftSkills] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedFluency, setSelectedFluency] = useState("");
  const [pendingSoftSkillId, setPendingSoftSkillId] = useState("");
  const [pendingSoftSkillEvidence, setPendingSoftSkillEvidence] = useState("");
  const [pendingEvidenceError, setPendingEvidenceError] = useState("");
  const [softSkillEvidenceErrors, setSoftSkillEvidenceErrors] = useState({});
  const [skillsMap, setSkillsMap] = useState({});
  const [openSkillsForRole, setOpenSkillsForRole] = useState(null);
  const [loadingSkills, setLoadingSkills] = useState(false);

  const token = localStorage.getItem("adminToken");
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3500";

  // Sync incoming data
  useEffect(() => {
    const dataRoles = Array.isArray(src) ? src : src?.roles;
    if (dataRoles?.length > 0) {
      const normalizedRoles = dataRoles.map(sanitizePreferredRoleForPayload);
      setRoles(normalizedRoles);
      normalizedRoles.forEach((role) => {
        if (role.function) fetchRolesForFunction(role.function, role.id);
      });
    }
    if (src?.softSkills?.length > 0) {
      setSoftSkills(src.softSkills.map(normalizeSoftSkill));
    }
    if (src?.languages?.length > 0) setLanguagesList(src.languages);
  }, [profileData]);

  useEffect(() => {
    if (!validationKey) return;
    const errors = {};
    softSkills.forEach((skill, index) => {
      if (!String(skill.evidence ?? "").trim()) {
        errors[index] = "Evidence / example is required.";
      }
    });
    setSoftSkillEvidenceErrors(errors);
  }, [validationKey, softSkills]);

  // Fetch job functions
  useEffect(() => {
    fetch(`${apiUrl}/api/profile/job-functions`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((r) => r.json())
      .then((d) => setLocalFunctions(d || []))
      .catch(console.error);
  }, []);

  // Fetch soft skills
  useEffect(() => {
    setLoadingSoftSkills(true);
    axiosInstance.get("api/job-postings/soft-skills/list")
      .then((r) => setSoftSkillList(r.data || []))
      .catch(console.error)
      .finally(() => setLoadingSoftSkills(false));
  }, []);

  const fetchRolesForFunction = async (functionId, roleCardId) => {
    setLoadingRoles((p) => ({ ...p, [roleCardId]: true }));
    try {
      const r = await fetch(`${apiUrl}/api/profile/job-roles/${functionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "ngrok-skip-browser-warning": "true",
        },
      });
      if (r.ok) {
        const json = await r.json();
        setRoleOptions((p) => ({ ...p, [roleCardId]: json }));
      }
    } catch (e) { console.error(e); }
    setLoadingRoles((p) => ({ ...p, [roleCardId]: false }));
  };

  const fetchSkillsForRole = async (functionId, roleId) => {
    setLoadingSkills(true);
    try {
      const r = await axiosInstance.get(`/api/profile/job-role-skills/${functionId}/${roleId}`);
      setSkillsMap(typeof r.data?.specific_skills === "object" ? r.data.specific_skills : {});
    } catch { setSkillsMap({}); }
    setLoadingSkills(false);
  };

  const emitChange = (overrides = {}) => {
    if (!onChange) return;
    const { roles: _ignoredRoles, ...safeOverrides } = overrides;
    // Admin only edits soft skills & languages — never resend preferred roles on save.
    onChange({
      softSkills: safeOverrides.softSkills ?? softSkills,
      languages: safeOverrides.languages ?? languagesList,
      ...safeOverrides,
    });
  };

  const updateRole = (id, field, value) => {
    const updated = roles.map((r) => (r.id === id ? { ...r, [field]: value } : r));
    setRoles(updated);
    emitChange({ roles: updated });
  };

  const addRole = () => {
    const newId = Math.max(...roles.map((r) => r.id), 0) + 1;
    const updated = [...roles, { id: newId, function: "", role: "", skills: [] }];
    setRoles(updated);
    emitChange({ roles: updated });
  };

  const deleteRole = (id) => {
    if (roles.length === 1) return;
    const updated = roles.filter((r) => r.id !== id);
    setRoles(updated);
    emitChange({ roles: updated });
  };

  const availableSoftSkills = softSkillList.filter(
    (skill) => !isSoftSkillSelected(softSkills, skill),
  );

  const availableLanguages = [...languageOptions]
    .sort((a, b) => a.localeCompare(b))
    .filter((lang) => !languagesList.some((item) => item.name === lang));

  const addSoftSkill = () => {
    const skillId = Number(pendingSoftSkillId);
    const skill = softSkillList.find((item) => Number(item.id) === skillId);
    const evidence = pendingSoftSkillEvidence.trim();

    if (!skill || isSoftSkillSelected(softSkills, skill)) return;

    if (!evidence) {
      setPendingEvidenceError("Evidence / example is required.");
      return;
    }

    const updated = [
      ...softSkills,
      normalizeSoftSkill({
        id: skill.id,
        softSkill: skill.name,
        description: skill.description,
        evidence,
      }),
    ];
    setSoftSkills(updated);
    setPendingSoftSkillId("");
    setPendingSoftSkillEvidence("");
    setPendingEvidenceError("");
    setSoftSkillEvidenceErrors({});
    emitChange({ softSkills: updated });
  };

  const addLanguage = () => {
    if (!selectedLanguage || !selectedFluency) return;
    if (languagesList.some((l) => l.name === selectedLanguage)) return;
    const updated = [...languagesList, { name: selectedLanguage, fluency: selectedFluency }];
    setLanguagesList(updated);
    setSelectedLanguage("");
    setSelectedFluency("");
    emitChange({ languages: updated });
  };

  const removeLanguage = (idx) => {
    const updated = languagesList.filter((_, i) => i !== idx);
    setLanguagesList(updated);
    emitChange({ languages: updated });
  };

  return (
    <div>
      {/* Fields below are visible in user-frontend, so they are commented out here 
      <h4 style={{ margin: "0 0 4px", color: "#1f2937", fontSize: 16, fontWeight: 700 }}>
        Preferred Roles &amp; Technical Skills
      </h4>
      <p className="cpe-helper" style={{ marginBottom: 20 }}>
        Select function and role the candidate wants to be searchable for.
      </p>

      {roles.map((role, index) => (
        <div key={role.id} className="cpe-role-card">
          <button
            type="button"
            className="cpe-role-delete-btn"
            onClick={() => deleteRole(role.id)}
            disabled={roles.length === 1}
          >
            ✕ Remove
          </button>

          <p className="cpe-role-card-title">Role {index + 1}</p>

          <div className="cpe-form-group">
            <label className="cpe-label">Function *</label>
            {localFunctions.length > 0 ? (
              <select
                className="cpe-select"
                value={role.function || ""}
                onChange={(e) => {
                  const val = Number(e.target.value) || "";
                  updateRole(role.id, "function", val);
                  updateRole(role.id, "role", "");
                  setRoleOptions((p) => ({ ...p, [role.id]: [] }));
                  if (val) fetchRolesForFunction(val, role.id);
                }}
              >
                <option value="">Select function</option>
                {localFunctions.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            ) : (
              <input className="cpe-input" disabled placeholder="Loading functions…" />
            )}
          </div>

          <div className="cpe-form-group">
            <label className="cpe-label">Role *</label>
            <select
              className="cpe-select"
              value={role.role || ""}
              disabled={!role.function || loadingRoles[role.id]}
              onChange={(e) => updateRole(role.id, "role", Number(e.target.value) || "")}
            >
              <option value="">
                {loadingRoles[role.id] ? "Loading roles…" : "Select role"}
              </option>
              {(roleOptions[role.id] || []).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          {role.function && role.role && (
            <div className="cpe-form-group">
              <label className="cpe-label">Technical Skills *</label>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={loadingSkills}
                onClick={() => {
                  (async () => {
                    await fetchSkillsForRole(role.function, role.role);
                    setOpenSkillsForRole(role.id);
                  })();
                }}
              >
                {loadingSkills ? "Loading…" : role.skills?.length > 0
                  ? `Edit Skills (${role.skills.length} selected)`
                  : "+ Add Technical Skills"}
              </button>
              {role.skills?.length > 0 && (
                <div className="cpe-chips" style={{ marginTop: 8 }}>
                  {role.skills.map((skillObj, si) => {
                    const name = Object.keys(skillObj)[0];
                    return (
                      <span key={si} className="cpe-chip">{name}</span>
                    );
                  })}
                </div>
              )}
              <SkillsDialog
                open={openSkillsForRole === role.id}
                onClose={() => setOpenSkillsForRole(null)}
                skillsMap={skillsMap}
                currentSkills={role.skills}
                onSave={(skills) => {
                  updateRole(role.id, "skills", skills);
                  setOpenSkillsForRole(null);
                }}
              />
            </div>
          )}
        </div>
      ))}

      <button type="button" className="btn btn-outline btn-sm" style={{ marginBottom: 28 }} onClick={addRole}>
        + Add Preferred Role
      </button>
      */}



      {/* ── Soft Skills ── */}
      <div className="cpe-form-group">
        <label className="cpe-label">Soft Skills</label>
        {loadingSoftSkills ? (
          <input className="cpe-input" disabled placeholder="Loading soft skills…" />
        ) : (
          <>
            <div className="cpe-soft-skill-add-block">
              <div className="cpe-add-row">
                <div className="cpe-add-field">
                  <span className="cpe-add-field-label">
                    Skill <span className="cpe-required">*</span>
                  </span>
                  <select
                    className="cpe-select"
                    value={pendingSoftSkillId}
                    onChange={(e) => {
                      setPendingSoftSkillId(e.target.value);
                      setPendingEvidenceError("");
                      if (!e.target.value) {
                        setPendingSoftSkillEvidence("");
                      }
                    }}
                    disabled={availableSoftSkills.length === 0}
                  >
                    <option value="">
                      {availableSoftSkills.length === 0
                        ? "All soft skills added"
                        : "Select a soft skill"}
                    </option>
                    {availableSoftSkills.map((skill) => (
                      <option key={skill.id} value={skill.id}>
                        {skill.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {pendingSoftSkillId && (
                <>
                  <div className="cpe-form-group" style={{ marginBottom: 12 }}>
                    <label className="cpe-label">
                      Evidence / Example <span className="cpe-required">*</span>
                    </label>
                    <textarea
                      className={`cpe-textarea${pendingEvidenceError ? " cpe-input-error" : ""}`}
                      placeholder="Provide an example for this soft skill"
                      value={pendingSoftSkillEvidence}
                      onChange={(e) => {
                        setPendingSoftSkillEvidence(e.target.value);
                        if (e.target.value.trim()) setPendingEvidenceError("");
                      }}
                      rows={3}
                      autoFocus
                    />
                    {pendingEvidenceError && (
                      <p className="cpe-field-error">{pendingEvidenceError}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={addSoftSkill}
                    disabled={!pendingSoftSkillEvidence.trim()}
                  >
                    Add skill
                  </button>
                </>
              )}
            </div>
            {availableSoftSkills.length === 0 && softSkills.length > 0 && (
              <p className="cpe-helper">Every available soft skill has been added.</p>
            )}
          </>
        )}

        {softSkills.length > 0 && (
          <div className="cpe-soft-skills-list">
            {softSkills.map((skill, index) => {
              const skillKey = getSoftSkillKey(skill) || `soft-skill-${index}`;
              return (
                <div key={skillKey} className="cpe-soft-skill-item">
                  <div className="cpe-soft-skill-body">
                    <p className="cpe-soft-skill-name">{skill.softSkill}</p>
                    <label className="cpe-label" style={{ marginBottom: 6 }}>
                      Evidence / Example <span className="cpe-required">*</span>
                    </label>
                    <textarea
                      className={`cpe-textarea${
                        softSkillEvidenceErrors[index] ? " cpe-input-error" : ""
                      }`}
                      placeholder="Provide an example for this soft skill"
                      value={skill.evidence || ""}
                      required
                      onChange={(e) => {
                        const updated = softSkills.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, evidence: e.target.value }
                            : item,
                        );
                        setSoftSkills(updated);
                        if (e.target.value.trim()) {
                          setSoftSkillEvidenceErrors((prev) => {
                            const next = { ...prev };
                            delete next[index];
                            return next;
                          });
                        }
                        emitChange({ softSkills: updated });
                      }}
                    />
                    {softSkillEvidenceErrors[index] && (
                      <p className="cpe-field-error">
                        {softSkillEvidenceErrors[index]}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="cpe-icon-btn danger cpe-soft-skill-remove"
                    title="Remove soft skill"
                    onClick={() => {
                      const updated = softSkills.filter((_, itemIndex) => itemIndex !== index);
                      setSoftSkills(updated);
                      emitChange({ softSkills: updated });
                    }}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Languages ── */}
      <div className="cpe-form-group">
        <label className="cpe-label">Languages</label>
        <div className="cpe-add-row cpe-lang-row">
          <div className="cpe-add-field">
            <span className="cpe-add-field-label">Language</span>
            <select
              className="cpe-select"
              value={selectedLanguage}
              onChange={(e) => {
                setSelectedLanguage(e.target.value);
                if (!e.target.value) setSelectedFluency("");
              }}
              disabled={availableLanguages.length === 0}
            >
              <option value="">
                {availableLanguages.length === 0
                  ? "All languages added"
                  : "Select language"}
              </option>
              {availableLanguages.map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
            </select>
          </div>
          <div className="cpe-add-field">
            <span className="cpe-add-field-label">Fluency</span>
            <select
              className="cpe-select"
              value={selectedFluency}
              onChange={(e) => setSelectedFluency(e.target.value)}
              disabled={!selectedLanguage}
            >
              <option value="">Select fluency</option>
              {fluencyLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm cpe-add-action-btn"
            onClick={addLanguage}
            disabled={!selectedLanguage || !selectedFluency}
          >
            Add
          </button>
        </div>

        {availableLanguages.length === 0 && languagesList.length > 0 && (
          <p className="cpe-helper">Every available language has been added.</p>
        )}

        {languagesList.length > 0 && (
          <div className="cpe-chips cpe-lang-chips">
            {languagesList.map((lang, index) => (
              <span key={`${lang.name}-${index}`} className="cpe-chip">
                {lang.name} ({lang.fluency})
                <button
                  type="button"
                  className="cpe-chip-remove"
                  aria-label={`Remove ${lang.name}`}
                  onClick={() => removeLanguage(index)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChooseRolesSection;
